import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../lib/supabase';

export interface PressRelease {
  id: number;
  title: string;
  content: string;
  thumbnail_url: string | null;
  file_url: string | null;
  file_name: string | null;
  file_size: number | null;
  author: string;
  department: string;
  view_count: number;
  is_published: boolean;
  created_at: string;
  updated_at: string;
}

interface PressReleaseContextType {
  releases: PressRelease[];
  loading: boolean;
  addRelease: (release: Omit<PressRelease, 'id' | 'view_count' | 'created_at' | 'updated_at'>) => Promise<void>;
  updateRelease: (id: number, release: Partial<PressRelease>) => Promise<void>;
  deleteRelease: (id: number) => Promise<void>;
  getRelease: (id: number) => PressRelease | undefined;
  incrementViewCount: (id: number) => Promise<void>;
  refreshReleases: () => Promise<void>;
}

const PressReleaseContext = createContext<PressReleaseContextType | undefined>(undefined);

export const PressReleaseProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [releases, setReleases] = useState<PressRelease[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchReleases = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('press_releases')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setReleases(data || []);
    } catch (error) {
      console.error('Error fetching press releases:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReleases();
  }, []);

  const addRelease = async (release: Omit<PressRelease, 'id' | 'view_count' | 'created_at' | 'updated_at'>) => {
    try {
      const { error } = await supabase
        .from('press_releases')
        .insert({
          ...release,
          view_count: 0
        });

      if (error) throw error;
      await fetchReleases();
    } catch (error) {
      console.error('Error adding press release:', error);
      throw error;
    }
  };

  const updateRelease = async (id: number, release: Partial<PressRelease>) => {
    try {
      const { error } = await supabase
        .from('press_releases')
        .update({ ...release, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;
      await fetchReleases();
    } catch (error) {
      console.error('Error updating press release:', error);
      throw error;
    }
  };

  const deleteRelease = async (id: number) => {
    try {
      const { error } = await supabase
        .from('press_releases')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setReleases(prev => prev.filter(r => r.id !== id));
    } catch (error) {
      console.error('Error deleting press release:', error);
      throw error;
    }
  };

  const getRelease = (id: number) => {
    return releases.find(r => r.id === id);
  };

  const incrementViewCount = async (id: number) => {
    try {
      const release = releases.find(r => r.id === id);
      if (!release) return;

      await supabase
        .from('press_releases')
        .update({ view_count: release.view_count + 1 })
        .eq('id', id);

      setReleases(prev =>
        prev.map(r => r.id === id ? { ...r, view_count: r.view_count + 1 } : r)
      );
    } catch (error) {
      console.error('Error incrementing view count:', error);
    }
  };

  const refreshReleases = async () => {
    await fetchReleases();
  };

  return (
    <PressReleaseContext.Provider value={{
      releases,
      loading,
      addRelease,
      updateRelease,
      deleteRelease,
      getRelease,
      incrementViewCount,
      refreshReleases
    }}>
      {children}
    </PressReleaseContext.Provider>
  );
};

export const usePressRelease = () => {
  const context = useContext(PressReleaseContext);
  if (context === undefined) {
    throw new Error('usePressRelease must be used within a PressReleaseProvider');
  }
  return context;
};
